import React, { useCallback, useReducer, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getUsers, deleteUser, getRoles } from "../../services/users/usersService";
import { roleLabel } from "../utils/Constants";

import CustomPaginator from "../../shared/components/paginator/CustomPaginator";
import ButtonNew from "../../shared/components/buttonNew/ButtonNew";
import InputSearch from "../../shared/components/inputSearch/InputSearch";
import SelectStatus from "../../shared/components/selectStatus/SelectStatus";
import ButtonFilter from "../../shared/components/buttonFilter/ButtonFilter";
import ButtonDownload from "../../shared/components/buttonDownload/ButtonDownload";
import ButtonEdit from "../../shared/components/buttonEdit/ButtonEdit";
// import ButtonDelete from "../../shared/components/buttonDelete/ButtonDelete";
import DeleteModal from "../../shared/components/deleteModal/DeleteModal";
import TableHeader from "../../shared/components/tableHeader/TableHeader";

const ITEMS_PER_PAGE = 10;
const TABLE_HEADERS = ['Nombre', 'Email', 'Rol', 'Estado', 'Acciones'];
const STATUS_OPTIONS = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
];

// Reducer
const initialState = {
    users: [],
    currentPage: 1,
    totalPages: 3,
    isLoading: false,
    error: null,
    deleteModalOpen: false,
    selectedUserId: null,
    filters: { emailFilter: '', statusFilter: '', roleFilter: '' }
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_USERS':
            return { ...state, users: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_PAGE':
            return { ...state, currentPage: action.payload };
        case 'SET_TOTAL_PAGES':
            return { ...state, totalPages: action.payload };
        case 'SET_FILTERS':
            return { ...state, filters: action.payload };
        case 'TOGGLE_DELETE_MODAL':
            return { 
                ...state, 
                deleteModalOpen: !state.deleteModalOpen,
                selectedUserId: action.payload 
            };
        default:
            return state;
    }
}

// Components

const UserTable = React.memo(({ users, onDelete, error }) => {
    if (error) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                    Error obteniendo los datos
                </td>
            </tr>
        );
    }
    if (users.length === 0) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                    No se encontraron registros
                </td>
            </tr>
        );
    }

    return users.map((user) => (
        <tr key={user.email} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
            <th scope="row" className="px-6 py-3 font-medium whitespace-nowrap">
                {user.firstName} {user.lastName}
            </th>
            <td className="px-6 py-3">{user.email}</td>
            <td className="px-6 py-3">{roleLabel(user.role)}</td>
            <td className="px-6 py-3">
                {user.active ? <span>Activo</span> : <span>Inactivo</span>}
            </td>
            <td className="flex px-6 py-3">
                <ButtonEdit to={`/users/${user.email}`} />
                {/* <ButtonDelete onClick={() => onDelete(user.email)} /> */}
            </td>
        </tr>
    ));
});

const FilterForm = React.memo(({ onSubmit, register, roleOptions }) => (
    <form onSubmit={onSubmit} className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 mb-2">
        <div className="flex items-center w-full md:flex-1 md:min-w-0">
            <InputSearch
                placeholder="Email" 
                name="emailFilter" 
                id="emailFilter"
                {...register('emailFilter')}
            />
        </div>
        <div className="flex items-center w-full md:flex-1 md:min-w-0">
            <SelectStatus
                id="roleFilter"
                name="roleFilter"
                placeholder="Seleccionar rol"
                options={roleOptions}
                {...register('roleFilter')}
            />
        </div>
        <div className="flex items-center w-full md:flex-1 md:min-w-0">
            <SelectStatus
                id="statusFilter" 
                name="statusFilter"
                placeholder="Seleccionar estado"
                options={STATUS_OPTIONS}
                {...register('statusFilter')}
            />
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-end w-full md:w-auto md:shrink-0 space-y-3 sm:space-y-0 sm:space-x-4">
            <ButtonFilter type="submit" />
            <ButtonDownload />
        </div>
    </form>
));

const UsersView = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { register, handleSubmit } = useForm();
    const [roleOptions, setRoleOptions] = useState([]);

    useEffect(() => {
        getRoles()
            .then((roles) => setRoleOptions(roles.map((role) => ({ label: roleLabel(role), value: role }))))
            .catch((error) => console.error('Error fetching roles:', error));
    }, []);

    const loadUsers = useCallback(async (filters = {}) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            dispatch({ type: 'SET_ERROR', payload: null });
            const data = await getUsers(state.currentPage, ITEMS_PER_PAGE, filters);
            dispatch({ type: 'SET_USERS', payload: data.content });
            dispatch({ type: 'SET_TOTAL_PAGES', payload: data.totalPages });
            
            if (data.totalPages < state.currentPage) {
                dispatch({ 
                    type: 'SET_PAGE', 
                    payload: data.totalPages === 0 ? 1 : data.totalPages 
                });
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            console.error('Error fetching users:', error);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [state.currentPage]);

    useEffect(() => {
        const newFilters = {};
        if (state.filters.emailFilter) newFilters.email = state.filters.emailFilter;
        if (state.filters.statusFilter) newFilters.active = state.filters.statusFilter;
        if (state.filters.roleFilter) newFilters.role = state.filters.roleFilter;
        loadUsers(newFilters);
    }, [state.filters, loadUsers]);

    const handleFilterSubmit = handleSubmit((data) => {
        dispatch({ type: 'SET_FILTERS', payload: data });
    });

    const handlePageChange = useCallback((page) => {
        if (page === state.currentPage || page < 1 || page > state.totalPages) return;
        dispatch({ type: 'SET_PAGE', payload: page });
    }, [state.currentPage, state.totalPages]);

    const toggleDeleteModal = useCallback((userId) => {
        dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: userId });
    }, []);

    const handleDelete = async () => {
        try {
            const response = await deleteUser(state.selectedUserId);
            if (response) {
                dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: null });
                await loadUsers();
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Error al eliminar el usuario' });
            console.error('Error al eliminar el usuario: ', error);
        }
    };

    return (
        <div className="flex flex-col p-3">
            <div className="flex max-sm:flex-col items-center justify-between mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">Usuarios</h1>
                <ButtonNew to="/users/newUser" />
            </div>

            <FilterForm onSubmit={handleFilterSubmit} register={register} roleOptions={roleOptions} />

            <hr className="my-2" />

            {state.isLoading ? (
                <div className="text-center py-4">Cargando...</div>
            ) : (
                <div className="relative overflow-x-auto shadow-lg sm:rounded-lg">
                    <table className="w-full text-sm text-left border border-gray-200">
                        <TableHeader headerOptions={TABLE_HEADERS}/>
                        <tbody>
                            <UserTable 
                                users={state.users} 
                                onDelete={toggleDeleteModal}
                                error={state.error}
                            />
                        </tbody>
                    </table>
                </div>
            )}

            {!state.error && (
                <CustomPaginator 
                    currentPage={state.currentPage} 
                    totalPages={state.totalPages} 
                    handlePageChange={handlePageChange}
                />
            )}

            <DeleteModal
                isOpen={state.deleteModalOpen}
                toggleDeleteModal={() => toggleDeleteModal(null)}
                deleteModal={handleDelete}
                nameParameter="usuario"
            />
        </div>
    );
};

export default UsersView;